import React, { useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { ScrollView, Image, useTheme } from 'tamagui';
import { CobuStack, CobuText, CobuButton, PerkDetailSkeleton, CobuActionButtons, CobuHeader, CobuBackButton } from '@cobu/ui';
import { useNavigate } from 'react-router-dom';
import { Check } from '@tamagui/lucide-icons';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { URLS } from '../../constants/Urls';
import { Strings } from '../../constants/Strings';
import { usePerkDetail } from '../../hooks/usePerkDetail';

const strings = Strings.get();

export default function PerkDetailScreen() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const heroColor = (location.state as any)?.heroColor as string | undefined;
  const params = useParams();
  const perkId = params.id as string;

  const {
    perk,
    redemptionsCount,
    isLoading,
    error,
    isRedeemed,
    isSaved,
    handleRedeemPress,
    handleSavePress,
    handleEmailPress,
    handlePhonePress,
    savePerkMutation,
    unsavePerkMutation,
    redeemPerkMutation,
  } = usePerkDetail({ perkId });

  // Error state
  const hasShownErrorRef = useRef(false);
  useEffect(() => {
    if (error && !hasShownErrorRef.current) {
      // Optionally display error toast here
      hasShownErrorRef.current = true;
    }
  }, [error]);

  // Loading state
  if (isLoading) {
    return (
      <PerkDetailSkeleton />
    );
  }

  if (error || !perk) {
    return null;
  }

  // UI Components
  const renderHeader = () => (
    <CobuStack
        bg={heroColor ? undefined : '$cobuTopaz'}
        h={'$56'}
        style={heroColor ? { backgroundColor: heroColor } : undefined}
    >
        <Image
          source={{
            uri: perk.merchant?.image || URLS.DEFAULT_MERCHANT_IMAGE,
          }}
          w="100%"
          h="100%"
          borderRadius="$3"
          objectFit="cover"
        />
        {isSaved && (
          <CobuStack 
            pos="absolute" 
            t="$4" 
            r="$4" 
            br="$5" 
            p="$2"
          >
            <Check size={24} color="$background" />
          </CobuStack>
        )}
    </CobuStack>
  );

  const renderPerkInfo = () => (
    <CobuStack 
      p="$4" 
      gap="$3" 
      bbw={1} 
      bbc="$backgroundMuted"
    >
      <CobuText 
        variant="heading" 
        fontSize="$6" 
        fontWeight="bold" 
        color="$color"
      >
        {perk.name}
      </CobuText>
      
      <CobuText 
        variant="body" 
        fontSize="$4" 
        fontWeight="bold" 
        color="$color"
      >
        {perk.merchant?.name}
      </CobuText>
      
      {perk.merchant?.category && (
        <CobuStack layout="horizontal" ai="center" gap="$2">
          <LocalOfferIcon htmlColor={theme.black?.val || '#000000'} fontSize="small" />
          <CobuText 
            variant="body" 
            fontSize="$3" 
            color="$cobuDarkBlue"
          >
            {perk.merchant.category.charAt(0).toUpperCase() + perk.merchant.category.slice(1)}
          </CobuText>
        </CobuStack>
      )}
    </CobuStack>
  );

  const renderRedemptionCount = () => {
    if (redemptionsCount < 3) return null;
    
    return (
      <CobuStack 
        p="$4" 
        bbw={1} 
        bbc="$backgroundMuted"
      >
        <CobuText 
          variant="body" 
          fontSize="$3" 
          color="$cobuTopaz" 
          fontWeight="medium"
        >
          {Strings.format(strings.perks.detail.enjoyedPerk, { count: String(redemptionsCount) })}
        </CobuText>
      </CobuStack>
    );
  };

  const renderDescription = () => {
    if (!perk.description) return null;
    
    return (
      <CobuStack p="$4" mb="$4">
        <CobuText 
          variant="body" 
          fontSize="$4" 
          color="$color"
        >
          {perk.description}
        </CobuText>
      </CobuStack>
    );
  };

  const renderActionButtons = () => {
    const buttons = [];
    
    const detailActions = strings.perks.detail.actions;

    // Save button for non-contact perks
    if (perk.offerType !== 'contact') {
      buttons.push({
        title: isSaved ? detailActions.saved : detailActions.saveToList,
        variant: 'secondary' as const,
        onPress: handleSavePress,
        disabled: savePerkMutation.isPending || unsavePerkMutation.isPending,
      });
    }

    // Action button based on offer type
    switch (perk.offerType) {
      case 'learn':
        buttons.push({
          title: detailActions.learnMore,
          variant: 'primary' as const,
          onPress: handleRedeemPress,
          disabled: redeemPerkMutation.isPending,
        });
        break;
      case 'custom':
        buttons.push({
          title: perk.customButtonLabel || detailActions.redeem,
          variant: 'primary' as const,
          onPress: handleRedeemPress,
          disabled: redeemPerkMutation.isPending,
        });
        break;
      case 'contact':
        // Contact buttons
        const contactButtons = [];

        contactButtons.push({
          title: detailActions.save,
          variant: 'secondary' as const,
          onPress: handleSavePress,
          disabled: savePerkMutation.isPending || unsavePerkMutation.isPending,
        });

        if (perk.contactEmail) {
          contactButtons.push({
            title: detailActions.email,
            variant: 'primary' as const,
            onPress: handleEmailPress,
          });
        }

        if (perk.contactPhone || perk.contactSMSNumber) {
          contactButtons.push({
            title: detailActions.phone,
            variant: 'primary' as const,
            onPress: handlePhonePress,
          });
        }

        return (
          <CobuStack px="$4" py="$3" btw={1} btc="$backgroundMuted" mb="$4">
            <CobuActionButtons
              buttons={contactButtons}
              direction="horizontal"
              responsive={false}
              gap="$2"
            />
          </CobuStack>
        );
      default:
        buttons.push({
          title: isRedeemed ? detailActions.redeemed : detailActions.redeem,
          variant: 'primary' as const,
          onPress: handleRedeemPress,
          disabled: redeemPerkMutation.isPending,
        });
        break;
    }

    return (
      <CobuStack px="$4" py="$3" btw={1} btc="$backgroundMuted" mb="$4">
        <CobuActionButtons
          buttons={buttons as any}
          direction="horizontal"
          responsive={false}
          gap="$2"
        />
      </CobuStack>
    );
  };

  return (
    <div style={{ background: 'white' }}>
      <CobuHeader title={perk?.name || strings.perks.detail.title} renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />} />
      <ScrollView showsVerticalScrollIndicator={false}>
        {renderHeader()}
        {renderPerkInfo()}
        {renderRedemptionCount()}
        {renderDescription()}
      </ScrollView>
      {renderActionButtons()}
    </div>
  );
} 